import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import React, { ButtonHTMLAttributes, ReactElement } from 'react';
import VoxelTooltipShort from '../Tooltip/VoxelTooltipShort';
import VoxelIconButton from '../Buttons/VoxelIconButton';
import VoxelText from '../Text/VoxelText';
import VoxelAlertIconButton from '../Alert/VoxelSeverityIconButton';
import { VoxelAlertSeverity } from '../Alert/VoxelAlert';

export interface VoxelChipProps
  extends Pick<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'onClick' | 'onMouseDown' | 'onMouseUp' | 'type' | 'disabled'
  > {
  title: string | React.ReactNode;
  icon?: ReactElement<FontAwesomeIconProps>;
  color?: VoxelAlertSeverity;
  colorClassName?: string;
  tooltip?: string;
  widthClass?: string;
  shape?: 'rounded' | 'square';
  textTransform?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  close?: boolean;
  closeTooltip?: string;
  textCenter?: boolean;
}

const VoxelChip: React.FC<VoxelChipProps> = ({
  onClick,
  onMouseDown,
  onMouseUp,
  title,
  disabled,
  icon,
  color = 'neutral',
  tooltip,
  widthClass = 'w-fit',
  shape = 'rounded',
  colorClassName,
  textTransform = 'lowercase',
  size = 'md',
  close,
  closeTooltip,
  textCenter
}) => {
  // ---- COLOR ----
  let textColor = '';
  let bgColor = '';
  let shadow = '';

  if (color === 'neutral') {
    bgColor = 'bg-voxel-gray-100';
    textColor = 'text-voxel-gray-700';
    shadow = 'shadow-none';
  }
  if (color === 'error') {
    bgColor = 'bg-error-100';
    textColor = 'text-error-700';
    shadow = 'shadow-none';
  }
  if (color === 'warning') {
    bgColor = 'bg-warning-500/20';
    textColor = 'text-warning-800';
    shadow = 'shadow-none';
  }
  if (color === 'success') {
    bgColor = 'bg-success-600/20';
    textColor = 'text-success-900';
    shadow = 'shadow-none';
  }
  if (color === 'info') {
    bgColor = 'bg-info-600/20';
    textColor = 'text-info-900';
    shadow = 'shadow-none';
  }

  let padding = `
      ${size === 'xs' ? 'px-1.5 py-1 gap-0.5' : ''}    
      ${size === 'sm' ? 'px-2 py-1 gap-1.5' : ''}
      ${size === 'md' ? 'px-2.5 gap-1.5' : ''}
      ${icon && 'pl-3'}
      ${close ? 'pr-[0.25rem] py-1 ' : ''}
      ${size === 'md' && !close ? 'py-[5.5px]' : ''}
    `;

  let fontSize = `
  ${size === 'xs' ? 'text-[10px]' : ''}
  ${size === 'sm' ? 'text-xs' : ''}
  ${size === 'md' ? 'text-xs' : ''}
  `;


  return (
    <VoxelTooltipShort message={tooltip} disabled={!tooltip || disabled}>
      <div
        className={`
              ${widthClass}
                select-none
                cursor-default
                transition duration-100
                inline-block
                ${shape === 'square' ? 'rounded-md' : ''} ${shape === 'rounded' ? 'rounded-xl' : ''}
                ${padding}
                ${bgColor} 
                ${shadow} 
                whitespace-nowrap
                font-sans ${fontSize} font-semibold leading-none
                ${textColor} 
                ${colorClassName}
                flex flex-row items-center justify-between
            `}
      >
        <div className={`flex-grow flex flex-row items-center ${textCenter ? 'justify-center' : 'justify-start'} gap-1`}>
          {icon && <div>{icon}</div>}
          <VoxelText variant="bread-semibold" className={`${textTransform} pb-[1px]`}>
            {title}
          </VoxelText>
        </div>
        {close && (
          <VoxelAlertIconButton
            onClick={onClick}
            disabled={disabled}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            color={color}
            icon={<FontAwesomeIcon icon={faXmark} size="xs" />}
            size={'xxxs'}
            textColorClass={colorClassName ? 'text-white/90' : textColor}
            hoverTextColorClass={colorClassName ? 'hover:text-white/90' : undefined}
            tooltip={closeTooltip}
          />
        )}
      </div>
    </VoxelTooltipShort>
  );
};

export default VoxelChip;
